import { Avatar, Box, Button, Chip, Divider, Drawer, IconButton, Typography } from '@mui/material'
import { FC, useState } from 'react'
import {
  MdClose,
  MdOpenInNew,
  MdCheckCircle,
  MdOutlineImage,
  MdAssignment,
  MdTimer,
} from 'react-icons/md'
import { BsFileEarmarkText } from 'react-icons/bs'
import { format, parseISO, differenceInDays } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { IKanbanBoardHCPArtesTask } from '../../KanbanBoardHCPArtworks/interfaces/kanban-board-hcp-artworks-schema.interface'

interface IArteDetalhesDrawerProps {
  task: IKanbanBoardHCPArtesTask | null
  onClose: () => void
}

function formatDate(dateStr: string | null | undefined, withTime = false): string {
  if (!dateStr) return '—'
  try {
    return format(parseISO(dateStr), withTime ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy', { locale: ptBR })
  } catch {
    return dateStr
  }
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      fontWeight={700}
      sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}
    >
      {children}
    </Typography>
  )
}

function InfoItem({ label, value, highlight = false }: { label: string; value: string; highlight?: boolean }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={highlight ? 700 : 500}
        color={highlight ? 'error.main' : 'text.primary'}
        sx={{ fontSize: '0.82rem' }}
      >
        {value}
      </Typography>
    </Box>
  )
}

function StatusIcon({ label, ok }: { label: string; ok: boolean }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
      {ok
        ? <MdCheckCircle size={13} color="#2e7d32" />
        : <MdOutlineImage size={13} color="#bdbdbd" />}
      <Typography
        variant="caption"
        sx={{ fontSize: '0.72rem', color: ok ? '#2e7d32' : 'text.disabled', fontWeight: ok ? 600 : 400 }}
      >
        {label}
      </Typography>
    </Box>
  )
}

export const ArteDetalhesDrawer: FC<IArteDetalhesDrawerProps> = ({ task, onClose }) => {
  const [imageError, setImageError] = useState(false)

  const content = task?.content

  const thumbnailSrc = content?.thumbnail_arte_final
    ? content.thumbnail_arte_final.includes('dropbox.com')
      ? content.thumbnail_arte_final
          .replace('dl=0', 'dl=1')
          .replace('www.dropbox.com', 'dl.dropboxusercontent.com')
      : content.thumbnail_arte_final
    : null

  const diasNaEtapa = content?.atualizado_em
    ? differenceInDays(new Date(), parseISO(content.atualizado_em))
    : 0

  return (
    <Drawer
      anchor="right"
      open={task !== null}
      onClose={onClose}
      PaperProps={{ sx: { width: 440, display: 'flex', flexDirection: 'column' } }}
    >
      {content && (
        <>
          {/* ── Cabeçalho fixo ─────────────────────────────────────── */}
          <Box sx={{ px: 2.5, pt: 2.5, pb: 2, borderBottom: '1px solid rgba(0,0,0,0.08)', flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" fontWeight={700} noWrap>
                  #{content.numero}
                </Typography>
                <Typography variant="body2" fontWeight={500} noWrap>
                  {content.cliente}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {content.produto_nome}
                </Typography>
              </Box>
              <IconButton size="small" onClick={onClose} sx={{ ml: 1, flexShrink: 0 }}>
                <MdClose size={20} />
              </IconButton>
            </Box>

            {/* Chips de status */}
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1.5 }}>
              {content.atrasado && (
                <Chip
                  label="Atrasado"
                  size="small"
                  sx={{ bgcolor: 'rgba(211,47,47,0.12)', color: '#b71c1c', fontWeight: 700, fontSize: '0.70rem' }}
                />
              )}
              {content.itens_pendentes === 0 ? (
                <Chip
                  label="Pedido completo"
                  size="small"
                  sx={{ bgcolor: 'rgba(46,125,50,0.12)', color: '#2e7d32', fontWeight: 600, fontSize: '0.70rem' }}
                />
              ) : (
                <Chip
                  label={`${content.itens_pendentes} pend. no pedido`}
                  size="small"
                  sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: '0.70rem' }}
                />
              )}
              {content.tem_os && content.ordem_de_servico && (
                <Chip
                  icon={<MdAssignment size={11} color="#1565c0" />}
                  label={`OS #${content.ordem_de_servico}`}
                  size="small"
                  sx={{ bgcolor: 'rgba(21,101,192,0.10)', color: '#1565c0', fontWeight: 600, fontSize: '0.70rem' }}
                />
              )}
              <Chip
                icon={<MdTimer size={11} color={diasNaEtapa > 5 ? '#e65100' : '#757575'} />}
                label={`${diasNaEtapa}d na etapa`}
                size="small"
                sx={{
                  bgcolor: diasNaEtapa > 5 ? '#fff3e0' : 'rgba(0,0,0,0.04)',
                  color: diasNaEtapa > 5 ? '#e65100' : 'text.secondary',
                  fontWeight: diasNaEtapa > 5 ? 700 : 400,
                  fontSize: '0.70rem',
                }}
              />
            </Box>
          </Box>

          {/* ── Corpo rolável ──────────────────────────────────────── */}
          <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            {/* Thumbnail + Botão arte final */}
            {(thumbnailSrc || content.link_arte_final) && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                {thumbnailSrc && !imageError && (
                  <Box
                    component="img"
                    src={thumbnailSrc}
                    alt="Arte final"
                    onError={() => setImageError(true)}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: 220,
                      objectFit: 'contain',
                      borderRadius: 1.5,
                      bgcolor: '#f5f5f5',
                      border: '1px solid rgba(0,0,0,0.08)',
                    }}
                  />
                )}
                {content.link_arte_final && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<MdOpenInNew size={15} />}
                    onClick={() => window.open(content.link_arte_final, '_blank', 'noopener,noreferrer')}
                    sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
                  >
                    Abrir Arte Final
                  </Button>
                )}
              </Box>
            )}

            {/* Informações básicas */}
            <Box>
              <SectionTitle>Informações</SectionTitle>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                <InfoItem label="Pedido" value={`#${content.pedido_id}`} />
                <InfoItem
                  label="Tiragem"
                  value={content.tiragem ? `${content.tiragem.toLocaleString('pt-BR')} un` : '—'}
                />
                {content.designer && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Avatar sx={{ width: 22, height: 22, fontSize: '0.70rem', bgcolor: '#84a802', flexShrink: 0 }}>
                      {content.designer.nome.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
                        Designer
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 500 }}>
                        {content.designer.nome}
                      </Typography>
                    </Box>
                  </Box>
                )}
                <InfoItem label="Perfil SLA" value={content.perfil_sla.nome} />
              </Box>
            </Box>

            {/* Etiquetas */}
            {content.etiquetas.length > 0 && (
              <>
                <Divider />
                <Box>
                  <SectionTitle>Etiquetas</SectionTitle>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {content.etiquetas.map((e) => (
                      <Chip
                        key={e.id}
                        label={e.nome}
                        size="small"
                        sx={{ bgcolor: e.cor, color: '#000', fontWeight: 600, fontSize: '0.70rem' }}
                      />
                    ))}
                  </Box>
                </Box>
              </>
            )}

            {/* Prazo e arquivos */}
            <Divider />
            <Box>
              <SectionTitle>Prazo e Arquivos</SectionTitle>
              {content.prazo_atual && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    Prazo da etapa
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={content.atrasado ? 700 : 500}
                    color={content.atrasado ? 'error.main' : 'text.primary'}
                    sx={{ fontSize: '0.85rem' }}
                  >
                    {formatDate(content.prazo_atual, true)}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <StatusIcon label="Briefing" ok={content.tem_briefing} />
                <StatusIcon label="Arte" ok={content.tem_arte} />
                <StatusIcon label="Arte Final" ok={!!content.link_arte_final} />
              </Box>
            </Box>

            {/* Produção */}
            {content.producao && (
              <>
                <Divider />
                <Box>
                  <SectionTitle>Produção</SectionTitle>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                    <InfoItem label="Etapa" value={content.producao.etapa || '—'} />
                    <InfoItem label="Início" value={formatDate(content.producao.data_inicio)} />
                    <InfoItem
                      label="Prazo de produção"
                      value={formatDate(content.producao.prazo_producao)}
                      highlight={
                        !!content.producao.prazo_producao &&
                        content.no_prazo_producao === false
                      }
                    />
                    <InfoItem label="Finalização" value={formatDate(content.producao.finalizacao)} />
                  </Box>
                </Box>
              </>
            )}

            {/* Revisões e Alterações */}
            {(content.revisoes_pendentes > 0 || content.alteracoes_aprovadas > 0 || content.alteracoes_entregues > 0) && (
              <>
                <Divider />
                <Box>
                  <SectionTitle>Revisões e Alterações</SectionTitle>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5 }}>
                    <InfoItem
                      label="Rev. pendentes"
                      value={String(content.revisoes_pendentes)}
                      highlight={content.revisoes_pendentes > 0}
                    />
                    <InfoItem label="Alt. aprovadas" value={String(content.alteracoes_aprovadas)} />
                    <InfoItem label="Alt. entregues" value={String(content.alteracoes_entregues)} />
                  </Box>
                </Box>
              </>
            )}

            {/* Histórico */}
            <Divider />
            <Box>
              <SectionTitle>Histórico</SectionTitle>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                <InfoItem label="Criado em" value={formatDate(content.criado_em)} />
                <InfoItem label="Atualizado em" value={formatDate(content.atualizado_em)} />
              </Box>
            </Box>

          </Box>
        </>
      )}
    </Drawer>
  )
}
